import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho thông tin hồ sơ người dùng
 */
export interface Profile {
  /** ID của hồ sơ */
  id: number;
  /** Họ và tên hiển thị */
  fullName: string;
  /** Đường dẫn ảnh đại diện */
  avatar?: string;
  /** Tiểu sử */
  bio?: string;
  /** URL website cá nhân */
  websiteUrl?: string;
  /** Địa chỉ */
  address?: string;
  /** Email hiển thị trong hồ sơ */
  email?: string;
  /** Số điện thoại */
  phone?: string;
  /** Giới tính */
  gender?: 'male' | 'female' | 'other';
  /** Ngày sinh */
  dateOfBirth?: string;
  /** Liên kết mạng xã hội */
  socialMedia?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
  /** Trạng thái kích hoạt hồ sơ */
  isActive: boolean;
  /** Thời gian tạo */
  createdAt: string;
  /** Thời gian cập nhật cuối */
  updatedAt: string;
  /** ID người dùng */
  userId: number;
}

/**
 * Interface cho thông tin người dùng
 */
export interface User {
  /** ID duy nhất của người dùng */
  id: number;
  /** Email của người dùng */
  email: string;
  /** Tên đầy đủ của người dùng (tùy chọn) */
  fullName?: string;
  /** Vai trò của người dùng trong hệ thống */
  role: 'GUEST' | 'MEMBER' | 'TOOL_USER' | 'PREMIUM' | 'MODERATOR' | 'ADMIN';
  /** Trạng thái hoạt động của tài khoản */
  isActive: boolean;
  /** Số dư token của người dùng */
  tokenBalance: number;
  /** Thông tin hồ sơ người dùng */
  profile?: Profile;
  /** Thời gian tạo tài khoản */
  createdAt: string;
  /** Thời gian cập nhật cuối */
  updatedAt: string;
}

/**
 * Interface cho yêu cầu cập nhật thông tin người dùng
 */
export interface UpdateUserRequest {
  /** Tên đầy đủ của người dùng (tùy chọn) */
  fullName?: string;
  /** Số điện thoại (tùy chọn) */
  phone?: string;
  /** Số dư token (tùy chọn) */
  tokenBalance?: number;
}

/**
 * Service xử lý các chức năng quản lý người dùng
 * Bao gồm xem, cập nhật vai trò, trạng thái và thông tin người dùng
 */
export class UsersService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  // ================== USERS (Admin) ==================
  
  /**
   * Lấy danh sách tất cả người dùng (admin)
   * @returns Promise<User[]> - Danh sách tất cả người dùng
   * @throws Error - Khi không thể tải danh sách người dùng
   */
  static async listUsers(): Promise<User[]> {
    const response = await fetch(`${this.baseUrl}/users`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách người dùng';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Cập nhật vai trò của người dùng (admin)
   * @param id - ID của người dùng cần cập nhật
   * @param role - Vai trò mới của người dùng
   * @returns Promise<any> - Kết quả cập nhật vai trò
   * @throws Error - Khi cập nhật vai trò thất bại
   */
  static async updateUserRole(id: number, role: 'GUEST' | 'MEMBER' | 'TOOL_USER' | 'PREMIUM' | 'MODERATOR' | 'ADMIN'): Promise<any> {
    const url = `${this.baseUrl}/admin/users/${id}/role`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ role }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật vai trò thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    
    const result = await response.json();
    return result;
  }

  /**
   * Cập nhật trạng thái hoạt động của người dùng (admin)
   * @param id - ID của người dùng cần cập nhật
   * @param isActive - Trạng thái hoạt động mới (true/false)
   * @returns Promise<any> - Kết quả cập nhật trạng thái
   * @throws Error - Khi cập nhật trạng thái thất bại
   */
  static async updateUserStatus(id: number, isActive: boolean): Promise<any> {
    const url = `${this.baseUrl}/admin/users/${id}/status`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật trạng thái thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    
    const result = await response.json();
    return result;
  }

  /**
   * Cập nhật thông tin cơ bản của người dùng (admin)
   * @param userId - ID của người dùng cần cập nhật
   * @param data - Thông tin cần cập nhật (tên, số điện thoại, token)
   * @returns Promise<any> - Kết quả cập nhật thông tin
   * @throws Error - Khi cập nhật thông tin thất bại
   */
  static async updateUser(userId: number, data: UpdateUserRequest): Promise<any> {
    const url = `${this.baseUrl}/admin/users/${userId}`;
    const headers = this.getAuthHeaders();
    
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật thông tin thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    
    return response.json();
  }

  /**
   * Cập nhật số dư token của người dùng (admin)
   * @param userId - ID của người dùng cần cập nhật
   * @param tokenBalance - Số dư token mới
   * @returns Promise<any> - Kết quả cập nhật số dư token
   * @throws Error - Khi cập nhật token thất bại
   */
  static async updateUserTokenBalance(userId: number, tokenBalance: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/admin/users/${userId}/tokens`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ tokenBalance }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật token thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xoá người dùng (admin)
   * @param id - ID người dùng cần xoá
   * @returns Promise<any> - Kết quả xoá người dùng
   * @throws Error - Khi xoá thất bại
   */
  static async deleteUser(id: number): Promise<any> {
    const url = `${this.baseUrl}/admin/users/${id}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xoá người dùng thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    const result = await response.json();
    return result;
  }

  /**
   * Lấy giao dịch theo user (admin)
   */
  static async listUserTransactions(userId: number, page = 1, limit = 10): Promise<{ data: any[]; total: number; page: number; limit: number; totalPages: number; }> {
    const response = await apiClient.get(`/admin/users/${userId}/transactions?page=${page}&limit=${limit}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải lịch sử giao dịch';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy số bài viết của 1 user (admin)
   */
  static async getUserPostCount(userId: number): Promise<number> {
    const response = await apiClient.get(`/posts/admin/count/${userId}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể lấy số lượng bài viết';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    const data = await response.json();
    return data?.total ?? 0;
  }
}
